import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type { TranslationRefreshReport } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

interface RefreshTranslationListInput {
  customerId?: string;
  stagedPurchaseOrderId?: string;
}

interface RefreshTranslationListOutput {
  reports: TranslationRefreshReport[];
}

export function useRefreshTranslationList(options?: {
  onSuccess?: (data: RefreshTranslationListOutput) => void;
}) {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation<
    RefreshTranslationListOutput,
    Error,
    RefreshTranslationListInput
  >({
    mutationFn: async (input) => {
      return await rpcClient.integrations.famous.refreshTranslationList(input);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.customerProducts.list.queryKey({
          input: {},
        }),
      });

      if (variables.customerId) {
        const totalCreated = data.reports.reduce((s, r) => s + r.created, 0);
        const totalUpdated = data.reports.reduce((s, r) => s + r.updated, 0);

        showToast({
          title: 'Translation list refreshed',
          message: `${data.reports.length} customer(s): ${totalCreated} created, ${totalUpdated} updated`,
          color: 'success',
          icon: 'check',
        });
      } else {
        showToast({
          title: 'Refresh started',
          message:
            'Translation list refresh is running in the background for all EDI customers.',
          color: 'success',
          icon: 'check',
        });
      }

      options?.onSuccess?.(data);
    },
    onError: (error) => {
      showToast({
        title: 'Refresh failed',
        message:
          error.message || 'Failed to refresh translation list from Famous',
        color: 'error',
        icon: 'x',
      });
    },
  });
}
